import { useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Toast } from 'antd-mobile';
import { Header } from '../components/Header';
import { t } from '../../../i18n';
import { TmsPageState } from '../types/tms';
import TmsTaskCardItem from '../components/TmsTaskCardItem';
import { TmsWaypointStepIndicator } from './components/TmsWaypointStepIndicator';
import { TmsContainerFormPopup } from './components/TmsContainerFormPopup';
import {
  GET_TMS_UNLOADING_DETAIL_SUMMARY,
  GetTmsUnloadingDetailSummaryResponse,
  SAVE_TMS_UNLOADING_DETAIL_INFO
} from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';
import { PdaPath } from '../../../enums/PdaNavigatePathEnum';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EditOutlined } from '@ant-design/icons';
import { getBottomNavBarContainerStyle } from '../../../utils/deviceUtils';
import { WaypointType, TmsTaskContainerType, TMS_CONTAINER_TYPE_ORDER, TmsOperationSourceEnum } from '../../../enums/TmsEnum';

function getContainerTypeName(type?: number | null): string {
  switch (type) {
    case TmsTaskContainerType.CARTON:
      return t('pda.pages.loadingDetail.containerTypeCardboardBox') || '卡板箱';
    case TmsTaskContainerType.BAG:
      return t('pda.pages.loadingDetail.containerTypeSnakeSkinBag') || '蛇皮袋';
    case TmsTaskContainerType.PALLET:
      return t('pda.pages.loadingDetail.containerTypePallet') || '托盘';
    case TmsTaskContainerType.GAYLORD:
      return t('pda.pages.loadingDetail.containerTypeGaylord') || 'Gaylord';
    default:
      return '-';
  }
}

export default function TmsUnLoadingDetailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as TmsPageState) || null;

  const taskId = state?.taskId ? parseInt(state.taskId) : NaN;
  const taskWayPointId = state?.taskWaypointId ? parseInt(state.taskWaypointId) : NaN;
  const isValidTaskId = !isNaN(taskId) && !isNaN(taskWayPointId);

  const { data, loading, error, refetch } = useQuery<GetTmsUnloadingDetailSummaryResponse>(
    GET_TMS_UNLOADING_DETAIL_SUMMARY,
    {
      variables: { taskId, taskWayPointId },
      skip: !isValidTaskId
    }
  );

  const [saveUnloadingDetailInfo, { loading: saveLoading }] = useMutation<{
    saveTmsUnloadingDetailInfo: boolean;
  }>(SAVE_TMS_UNLOADING_DETAIL_INFO);

  const [containerPopupVisible, setContainerPopupVisible] = useState(false);
  const [containerForm, setContainerForm] = useState<Record<number, string>>(
    Object.fromEntries(TMS_CONTAINER_TYPE_ORDER.map(type => [type, '']))
  );
  const [containerPopupMode, setContainerPopupMode] = useState<'add' | 'edit'>('add');
  // 本地暂存的实卸数据，添加/编辑只存本地，点击「完成卸车」才提交
  const [localUnloadingContainers, setLocalUnloadingContainers] = useState<Record<number, number> | null>(null);

  useEffect(() => {
    if (error) {
      Toast.show({
        content: t('pda.common.loadFailed') || 'Failed to load data',
        position: 'center',
        duration: 2000
      });
    }
  }, [error]);

  // 重新进入页面时清空保存状态，避免多次进入互相影响
  useEffect(() => {
    setLocalUnloadingContainers(null);
    setContainerPopupVisible(false);
  }, [taskId, taskWayPointId]);

  if (!state) {
    return null;
  }

  const { backRoute, ...taskData } = state;
  const task = taskData.task;
  const summary = data?.getTmsUnloadingDetailSummary;
  const rows = summary?.rows ?? [];

  // 展示的实卸数据：优先用本地暂存，否则用服务端（containerType 为 null 时仅用服务端）
  const getDisplayedActualCount = (containerType: number | null) =>
    containerType != null
      ? (localUnloadingContainers?.[containerType] ?? rows.find(r => r.containerType === containerType)?.actualCount ?? 0)
      : (rows.find(r => r.containerType === null)?.actualCount ?? 0);

  // 展示行：按类型 1-4 顺序，最后为容器类型为空的一组，expected 或 actual 有值则显示
  const displayRows = (() => {
    const hasAnyData = rows.length > 0 || localUnloadingContainers !== null;
    if (!hasAnyData) return [];
    const typeOrder: (number | null)[] = [...TMS_CONTAINER_TYPE_ORDER, null];
    return typeOrder.map(type => {
      const row = rows.find(r => r.containerType === type);
      return {
        containerType: type,
        expectedCount: row?.expectedCount ?? 0,
        actualCount: getDisplayedActualCount(type),
        sortingCode: row?.sortingCode ?? null
      };
    }).filter(r => r.expectedCount > 0 || r.actualCount > 0);
  })();

  const hasActualUnloadingData =
    localUnloadingContainers !== null
      ? Object.values(localUnloadingContainers).some(v => v > 0)
      : rows.some(r => r.actualCount > 0);
  const allActualEmpty = !hasActualUnloadingData;

  const handleOpenContainerPopup = () => {
    const fromRows =
      rows.length > 0
        ? Object.fromEntries(
            TMS_CONTAINER_TYPE_ORDER.map(type => [type, rows.find(r => r.containerType === type)?.actualCount ?? 0])
          )
        : null;
    const source = localUnloadingContainers ?? fromRows;
    if (source && Object.values(source).some(v => v > 0)) {
      setContainerPopupMode('edit');
      setContainerForm(
        Object.fromEntries(TMS_CONTAINER_TYPE_ORDER.map(type => [type, (source[type] ?? 0).toString()]))
      );
    } else {
      setContainerPopupMode('add');
      setContainerForm(Object.fromEntries(TMS_CONTAINER_TYPE_ORDER.map(type => [type, ''])));
    }
    setContainerPopupVisible(true);
  };

  const handleConfirmContainerPopup = () => {
    const items: { containerType: number; containerCount: number }[] = [];
    let totalCount = 0;

    for (const type of TMS_CONTAINER_TYPE_ORDER) {
      const value = containerForm[type];
      if (value === '' || value == null) {
        items.push({ containerType: type, containerCount: 0 });
        continue;
      }

      const count = Number(value);
      if (Number.isNaN(count) || count < 0) {
        Toast.show({
          content: t('pda.pages.loadingDetail.containerCountNonNegative') || '填写的容器数量必须大于等于0',
          position: 'center',
          duration: 2000
        });
        return;
      }
      items.push({ containerType: type, containerCount: count });
      totalCount += count;
    }

    if (totalCount === 0) {
      Toast.show({
        content: t('pda.pages.loadingDetail.containerCountMustBeGreaterThanZero') || '容器总数必须大于0',
        position: 'center',
        duration: 2000
      });
      return;
    }

    setLocalUnloadingContainers(Object.fromEntries(items.map(i => [i.containerType, i.containerCount])));
    setContainerPopupVisible(false);
  };

  const currentWaypointSortingCode =
    task?.waypoints?.find(w => Number(w.id) === taskWayPointId)?.waypointSortingCode ?? null;

  const handleCompleteUnloading = async () => {
    const itemsToSend =
      localUnloadingContainers !== null
        ? Object.entries(localUnloadingContainers)
            .filter(([, c]) => c > 0)
            .map(([type, count]) => ({
              containerType: Number(type),
              containerCount: count,
              sortingCode: currentWaypointSortingCode
            }))
        : displayRows
            .filter(r => r.actualCount > 0 && r.containerType != null)
            .map(r => ({
              containerType: r.containerType!,
              containerCount: r.actualCount,
              sortingCode: r.sortingCode ?? currentWaypointSortingCode
            }));

    if (itemsToSend.length === 0) {
      Toast.show({
        content: t('pda.pages.unloadingDetail.pleaseEnterActualUnloadingInfo') || '请填写实际卸车信息',
        position: 'center',
        duration: 2000
      });
      return;
    }

    try {
      if (localUnloadingContainers === null) {
        const isDestination = task?.waypointType === WaypointType.DESTINATION;
        if (isDestination) {
          navigate(PdaPath.PDA_TMS_UPLOAD_BOL, { state: { backRoute, ...taskData } });
        } else {
          if (backRoute) navigate(backRoute);
          else navigate(-1);
        }
        return;
      }

      await saveUnloadingDetailInfo({
        variables: {
          input: {
            taskId,
            taskWaypointId: taskWayPointId,
            items: itemsToSend,
            operationSource: TmsOperationSourceEnum.PDA,
          }
        }
      });
      await refetch();
      setLocalUnloadingContainers(null);
      const isDestination = task?.waypointType === WaypointType.DESTINATION;
      if (isDestination) {
        navigate(PdaPath.PDA_TMS_UPLOAD_BOL, { state: { backRoute, ...taskData } });
      } else {
        if (backRoute) navigate(backRoute);
        else navigate(-1);
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      Toast.show({
        content: e?.message || t('pda.common.loadFailed') || 'Failed to save',
        position: 'center',
        duration: 2000
      });
    }
  };

  return (
    <div className="w-full h-screen flex flex-col gap-2 bg-gray-50">
      <div className="p-3 flex flex-col gap-4 flex-1 overflow-y-auto pb-20">
        <Header title={t('pda.pages.shipOut.unloading')} backRoute={backRoute} />
        <div className="flex flex-col gap-3">
          {task && task.waypointType === WaypointType.DESTINATION && (
            <TmsWaypointStepIndicator currentStep={1} variant="unloading" />
          )}
          {task && <TmsTaskCardItem task={task} showButtons={false} />}

          {/* 卸车明细卡片 */}
          {loading ? (
            <div className="bg-white rounded-lg px-4 py-1 shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.08),0_2px_4px_-1px_rgba(0,0,0,0.08)] flex items-center justify-center">
              <div>{t('pda.common.loading') || 'Loading...'}</div>
            </div>
          ) : (
            <div className="bg-white rounded-lg px-4 py-1 shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.08),0_2px_4px_-1px_rgba(0,0,0,0.08)]">
              <div className="flex flex-col gap-3">
                <div className="flex gap-4">
                  <div className="flex-1 text-center">
                    <span className="text-sm font-semibold text-black">
                      {t('pda.pages.unloadingDetail.expectedQuantity')}
                    </span>
                  </div>
                  <div className="flex-1 text-center">
                    <span className="text-sm font-semibold text-black">
                      {t('pda.pages.unloadingDetail.actualQuantity')}
                    </span>
                  </div>
                </div>
                {displayRows.length > 0 ? (
                  <div className="flex gap-4">
                    <div className="flex-1 text-center flex flex-col gap-1">
                      {displayRows.map((row, idx) => (
                        <span
                          key={idx}
                          className="text-sm text-black"
                        >
                          {row.containerType == null
                            ? String(row.expectedCount)
                            : `${row.expectedCount}/${getContainerTypeName(row.containerType)}`}
                        </span>
                      ))}
                    </div>
                    <div className="flex-1 text-center flex flex-col items-center justify-center gap-1">
                      {hasActualUnloadingData ? (
                        <div className="flex items-start gap-2 w-full justify-center">
                          <div className="flex flex-col gap-1 flex-1 min-w-0">
                            {displayRows.map((row, idx) => (
                              <span
                                key={idx}
                                className="text-sm text-purple-600"
                              >
                                {row.containerType == null
                                  ? String(row.actualCount)
                                  : `${row.actualCount}/${getContainerTypeName(row.containerType)}`}
                              </span>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="flex-shrink-0 p-1 text-gray-500 hover:text-purple-600"
                            onClick={handleOpenContainerPopup}
                            aria-label={t('pda.pages.unloadingDetail.editActualUnloadingInfo')}
                          >
                            <EditOutlined className="text-lg" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center px-2 py-1 rounded border border-dashed border-red-400 bg-red-50 text-xs font-medium text-red-500"
                          onClick={handleOpenContainerPopup}
                        >
                          +  {t('pda.pages.unloadingDetail.pleaseEnterActualUnloadingInfo')}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-4">
                    <div className="flex-1 text-center">
                      <span className="text-base font-semibold text-gray-500">-/-</span>
                    </div>
                    <div className="flex-1 text-center flex flex-col items-center justify-center">
                      {allActualEmpty ? (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center px-2 py-1 rounded border border-dashed border-red-400 bg-red-50 text-xs font-medium text-red-500"
                          onClick={handleOpenContainerPopup}
                        >
                          +  {t('pda.pages.unloadingDetail.pleaseEnterActualUnloadingInfo')}
                        </button>
                      ) : (
                        <span className="text-base font-semibold text-gray-500">-/-</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 实卸信息 Popup */}
      <TmsContainerFormPopup
        visible={containerPopupVisible}
        mode={containerPopupMode}
        containerForm={containerForm}
        onFormChange={setContainerForm}
        onConfirm={handleConfirmContainerPopup}
        onCancel={() => setContainerPopupVisible(false)}
        editTitle="pda.pages.unloadingDetail.editActualUnloadingInfo"
        addTitle="pda.pages.unloadingDetail.addActualUnloadingInfo"
      />

      {/* 底部按钮：途径点仅显示完成卸车；终点显示上一步、下一步 */}
      <div {...getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20')}>
        {task?.waypointType === WaypointType.DESTINATION ? (
          <div className="flex gap-2">
            <Button
              block
              color="primary"
              loading={saveLoading}
              onClick={handleCompleteUnloading}
              style={{ flex: 1 }}
            >
              {t('pda.common.nextStep') || '下一步'}
            </Button>
          </div>
        ) : (
          <Button
            block
            color="primary"
            loading={saveLoading}
            onClick={handleCompleteUnloading}
          >
            {t('pda.pages.unloadingDetail.completeUnloading')}
          </Button>
        )}
      </div>
    </div>
  );
}
